import { useCallback, useMemo } from 'react';
import { isNil } from 'lodash';
import { DefaultValue, RecoilState, useRecoilCallback, useRecoilValue } from 'recoil';
import {
  aiFilterState,
  cardIdsState,
  cardVisibleState,
  columnsState,
  configState,
  drawFilterEnabledState,
  drawFilterFeaturesState,
  floorPlanMapBoundsState,
  floorPlanMapCenterState,
  floorPlanMapZoomLevelState,
  forcedClusteringState,
  forceMapReloadState,
  initialTableLoadState,
  languageState,
  mapBoundsState,
  mapCardHeightState,
  mapCenterState,
  mapExtentFilterEnabledState,
  mapLocalBoundsState,
  mapSelectBoundsState,
  mapTypeState,
  mapZoomLevelState,
  menuSelectionsState,
  quickSearchState,
  recordFilterState,
  selectedFloorPlanState,
  selectedItemsState,
  selectedProjectsState,
  selectedRecordTypeState,
  sortState,
  visibleRecordTypeKeysState,
} from '../recoil/atoms';
import {
  getValuesAsyncCallback,
  getValuesCallback,
  RecoilStateFamily,
  RecoilStateReference,
  RecoilValuesRecord,
  setValueAsyncCallback,
  setValueCallback,
  setValuesAsyncCallback,
  setValuesCallback,
} from '../recoil/callbacks';

// Note: Using unique static keys here, rather than deriving from `RecoilState['key']`, so that changes in `key` or `state` object
//  reference don't break loading viewstate from older Saved Views (also, some Recoil replacement packages don't use string keys).
//  When adding new states, ensure new keys are unique, and not reused from previously removed states. If needed, comment out instead.
const enum ViewstateKey {
  AIFilter,
  CardIDs,
  CardVisible,
  Columns,
  DrawFilterEnabled,
  DrawFilterFeatures,
  DrawToolEnabled,
  FloorPlanMapBounds,
  FloorPlanMapCenter,
  FloorPlanMapZoomLevel,
  ForcedClustering,
  Language,
  MapBounds,
  MapCardHeight,
  MapCenter,
  MapExtentFilterEnabled,
  MapLocalBounds,
  MapSelectBounds,
  MapType,
  MapZoomLevel,
  MenuSelections,
  QuickSearch,
  RecordFilter,
  SelectedFloorPlan,
  SelectedItems,
  SelectedProjects,
  SelectedRecordType,
  Sort,
  VisibleRecordTypeKeys,
}
type ViewstateKeyType = keyof typeof ViewstateKey;

export type ViewstateReference<TKey extends string = ViewstateKeyType> = RecoilStateReference<TKey>;
export type Viewstate = RecoilValuesRecord<ViewstateKeyType> & {
  [key: string]: RecoilValuesRecord[string];
};

export type UseViewstateReturnType = {
  getViewstate: () => Viewstate;
  getViewstateAsync: () => Promise<Viewstate>;
  setViewstate: (newValues: Partial<Viewstate>) => void;
  setViewstateAsync: (newValues: Partial<Viewstate>) => Promise<void>;
  resetViewstate: (overrideValues?: Partial<Viewstate>) => void;
  resetViewstateAsync: (overrideValues?: Partial<Viewstate>) => Promise<void>;
};

export const useViewstate = (): UseViewstateReturnType => {
  const { aggregateDefinitions } = useRecoilValue(configState);

  const recordTypeNames = useMemo(() => aggregateDefinitions.map(a => a.name), [aggregateDefinitions]);

  // TODO: How to omit (or match) `key` without sacrificing type safety for object key, `state`, and `params`?
  const recoilStates = useMemo(
    () =>
      ({
        // AIFilter: { key: 'AIFilter', state: aiFilterState },
        CardIDs: { key: 'CardIDs', state: cardIdsState },
        CardVisible: { key: 'CardVisible', state: cardVisibleState },
        Columns: { key: 'Columns', state: columnsState },
        DrawFilterEnabled: { key: 'DrawFilterEnabled', state: drawFilterEnabledState },
        DrawFilterFeatures: { key: 'DrawFilterFeatures', state: drawFilterFeaturesState },
        // DrawToolEnabled: { key: 'DrawToolEnabled', state: drawToolEnabledState },
        FloorPlanMapBounds: { key: 'FloorPlanMapBounds', state: floorPlanMapBoundsState },
        FloorPlanMapCenter: { key: 'FloorPlanMapCenter', state: floorPlanMapCenterState },
        FloorPlanMapZoomLevel: { key: 'FloorPlanMapZoomLevel', state: floorPlanMapZoomLevelState },
        ForcedClustering: { key: 'ForcedClustering', state: forcedClusteringState },
        Language: { key: 'Language', state: languageState },
        MapBounds: { key: 'MapBounds', state: mapBoundsState },
        MapCardHeight: { key: 'MapCardHeight', state: mapCardHeightState },
        MapCenter: { key: 'MapCenter', state: mapCenterState },
        MapExtentFilterEnabled: { key: 'MapExtentFilterEnabled', state: mapExtentFilterEnabledState },
        MapLocalBounds: { key: 'MapLocalBounds', state: mapLocalBoundsState },
        MapSelectBounds: { key: 'MapSelectBounds', state: mapSelectBoundsState },
        MapType: { key: 'MapType', state: mapTypeState },
        MapZoomLevel: { key: 'MapZoomLevel', state: mapZoomLevelState },
        MenuSelections: { key: 'MenuSelections', state: menuSelectionsState },
        QuickSearch: { key: 'QuickSearch', state: quickSearchState },
        RecordFilter: { key: 'RecordFilter', state: recordFilterState, params: recordTypeNames },
        SelectedFloorPlan: { key: 'SelectedFloorPlan', state: selectedFloorPlanState },
        SelectedItems: { key: 'SelectedItems', state: selectedItemsState },
        SelectedProjects: { key: 'SelectedProjects', state: selectedProjectsState },
        SelectedRecordType: { key: 'SelectedRecordType', state: selectedRecordTypeState },
        Sort: { key: 'Sort', state: sortState },
        VisibleRecordTypeKeys: { key: 'VisibleRecordTypeKeys', state: visibleRecordTypeKeysState },
      } satisfies Partial<Record<ViewstateKeyType, ViewstateReference>>),
    [recordTypeNames]
  );

  const recoilStateReferences = useMemo<ViewstateReference[]>(
    () =>
      (Object.entries(recoilStates) as [ViewstateKeyType, ViewstateReference][]).map(([key, recoilState]) => ({
        ...recoilState,
        key,
      })),
    [recoilStates]
  );

  const recoilStateMap = useMemo<ReadonlyMap<ViewstateKeyType, ViewstateReference>>(
    () =>
      new Map(
        recoilStateReferences.map(({ key, state, params }) => [
          key,
          !isNil(params)
            ? { key, state: state as RecoilStateFamily, params }
            : { key, state: state as RecoilState<unknown> },
        ])
      ),
    [recoilStateReferences]
  );

  const setRecoilValue = useRecoilCallback(setValueCallback(), []);
  const setRecoilValueAsync = useRecoilCallback(setValueAsyncCallback(), []);

  const getRecoilViewstate = useRecoilCallback(getValuesCallback<Viewstate>(recoilStateReferences), [
    recoilStateReferences,
  ]);
  const getRecoilViewstateAsync = useRecoilCallback(getValuesAsyncCallback<Viewstate>(recoilStateReferences), [
    recoilStateReferences,
  ]);
  const setRecoilViewstate = useRecoilCallback(setValuesCallback<Partial<Viewstate>>(recoilStateMap), [recoilStateMap]);
  const setRecoilViewstateAsync = useRecoilCallback(setValuesAsyncCallback<Partial<Viewstate>>(recoilStateMap), [
    recoilStateMap,
  ]);

  const setViewstate = useCallback<UseViewstateReturnType['setViewstate']>((newValues) => {
    setRecoilViewstate(newValues);
    setRecoilValue(forceMapReloadState, true);
    setRecoilValue(initialTableLoadState, true);
  }, [setRecoilViewstate, setRecoilValue]);

  const setViewstateAsync = useCallback<UseViewstateReturnType['setViewstateAsync']>(async (newValues) => {
    await setRecoilViewstateAsync(newValues);
    await Promise.all([
      setRecoilValueAsync(forceMapReloadState, true),
      setRecoilValueAsync(initialTableLoadState, true),
    ]);
  }, [setRecoilViewstateAsync, setRecoilValueAsync]);

  const getDefaultValues = useCallback((overrideValues?: Partial<Viewstate>): Viewstate => {
    return recoilStateReferences.reduce((acc, { key }) => {
      // TODO: Static default values. Could include with `ViewstateReference`, `recoilStates`; however, `undefined` would coalesce.
      acc[key] = overrideValues?.[key] ?? new DefaultValue();
      return acc;
    }, {} as Viewstate);
  }, [recoilStateReferences]);

  const resetViewstate = useCallback<UseViewstateReturnType['resetViewstate']>((overrideValues) => {
    setViewstate(getDefaultValues(overrideValues));
  }, [setViewstate, getDefaultValues]);

  const resetViewstateAsync = useCallback<UseViewstateReturnType['resetViewstateAsync']>(async (overrideValues) => {
    await setViewstateAsync(getDefaultValues(overrideValues));
  }, [setViewstateAsync, getDefaultValues]);

  return useMemo(() => ({
    getViewstate: getRecoilViewstate,
    getViewstateAsync: getRecoilViewstateAsync,
    setViewstate,
    setViewstateAsync,
    resetViewstate,
    resetViewstateAsync,
  }), [getRecoilViewstate, getRecoilViewstateAsync, setViewstate, setViewstateAsync, resetViewstate, resetViewstateAsync]);
};

export default useViewstate;
